import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapPin, X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useMunicipios, type Municipio } from '@/hooks/useMunicipios';

interface MunicipioMultiSelectProps {
  values: string[]; // array of codigo_ibge
  onChange: (codes: string[]) => void;
  placeholder?: string;
}

export const MunicipioMultiSelect: React.FC<MunicipioMultiSelectProps> = ({
  values,
  onChange,
  placeholder = 'Adicionar cidade de execução...',
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: municipios = [] } = useMunicipios();

  const selectedMunicipios = useMemo(
    () => municipios.filter(m => values.includes(m.codigo_ibge)),
    [municipios, values]
  );

  const filtered = useMemo(() => {
    const available = municipios.filter(m => !values.includes(m.codigo_ibge));
    if (!search.trim()) return available.slice(0, 15);
    const q = search.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return available
      .filter(m => {
        const nome = m.nome.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const uf = m.uf.toLowerCase();
        return nome.includes(q) || uf.includes(q);
      })
      .slice(0, 15);
  }, [municipios, values, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAdd = (mun: Municipio) => {
    onChange([...values, mun.codigo_ibge]);
    setSearch('');
  };

  const handleRemove = (code: string) => {
    onChange(values.filter(v => v !== code));
  };

  return (
    <div ref={containerRef} className="space-y-2">
      {/* Selected badges */}
      {selectedMunicipios.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedMunicipios.map(m => (
            <Badge key={m.codigo_ibge} variant="secondary" className="gap-1 pr-1">
              <MapPin className="h-3 w-3" />
              {m.nome} – {m.uf}
              <button
                type="button"
                onClick={() => handleRemove(m.codigo_ibge)}
                className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-8"
          placeholder={placeholder}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="relative">
          <div className="absolute z-50 w-full rounded-md border bg-popover shadow-lg max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Nenhum município encontrado
              </div>
            ) : (
              filtered.map(mun => (
                <button
                  key={mun.codigo_ibge}
                  type="button"
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-left"
                  onClick={() => handleAdd(mun)}
                >
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium">{mun.nome}</span>
                  <span className="text-muted-foreground">– {mun.uf}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
